import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
} from "@mui/material";
import api from "../../../services/api";

const productTypeOptions = [
  "specialtyCake",
  "cheesecake",
  "standardCake",
  "patisserie",
  "grazeBox",
];

const defaultFormState = {
  name: "",
  displayName: "",
  image: "",
  originalProductId: "",
  price: "",
  originalPrice: "",
  quantity: 1,
  trigger: {
    productTypes: [],
    productIds: [],
    minSubtotal: "",
  },
  category: "",
  type: "",
  maxPerOrder: 1,
  isActive: true,
  notes: "",
};

const UpsellItemFormModal = ({ open, onClose, onSave, initialData = null }) => {
  const [formData, setFormData] = useState(defaultFormState);
  const [errors, setErrors] = useState({});
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData });
    } else {
      setFormData(defaultFormState);
    }
  }, [initialData, open]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get("/products/all");
        setAllProducts(res.data);
      } catch (err) {
        console.error("Failed to fetch products", err);
      }
    };

    fetchProducts();
  }, []);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.price || isNaN(formData.price))
      newErrors.price = "Valid discounted price is required";
    if (
      !formData.trigger.productTypes ||
      formData.trigger.productTypes.length === 0
    )
      newErrors["trigger.productTypes"] = "Select at least one product type";

    return newErrors;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTriggerChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        [key]: value,
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSave(formData);
    setFormData(defaultFormState);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? "Edit Upsell Item" : "Add Upsell Item"}
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            fullWidth
            required
            error={!!errors.name}
            helperText={errors.name}
          />

          <TextField
            label="Display Name"
            value={formData.displayName}
            onChange={(e) => handleChange("displayName", e.target.value)}
            fullWidth
          />

          <TextField
            label="Image URL"
            value={formData.image}
            onChange={(e) => handleChange("image", e.target.value)}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Original Product</InputLabel>
            <Select
              value={formData.originalProductId}
              onChange={(e) =>
                handleChange("originalProductId", e.target.value)
              }
              fullWidth
              displayEmpty>
              <MenuItem value="">None</MenuItem>
              {allProducts.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.displayName || p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Discounted Price"
            type="number"
            value={formData.price}
            onChange={(e) => handleChange("price", parseFloat(e.target.value))}
            fullWidth
            required
            error={!!errors.price}
            helperText={errors.price}
          />

          <TextField
            label="Original Price (optional)"
            type="number"
            value={formData.originalPrice}
            onChange={(e) =>
              handleChange("originalPrice", parseFloat(e.target.value))
            }
            fullWidth
          />

          <TextField
            label="Default Quantity"
            type="number"
            value={formData.quantity}
            onChange={(e) => handleChange("quantity", parseInt(e.target.value))}
            fullWidth
          />

          <FormControl fullWidth error={!!errors["trigger.productTypes"]}>
            <InputLabel>Trigger Product Types</InputLabel>
            <Select
              multiple
              value={formData.trigger.productTypes || []}
              onChange={(e) =>
                handleTriggerChange("productTypes", e.target.value)
              }
              input={<OutlinedInput label="Trigger Product Types" />}
              renderValue={(selected) => selected.join(", ")}>
              {productTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox
                    checked={formData.trigger.productTypes.includes(type)}
                  />
                  <ListItemText primary={type} />
                </MenuItem>
              ))}
            </Select>
            {errors["trigger.productTypes"] && (
              <FormHelperText>{errors["trigger.productTypes"]}</FormHelperText>
            )}
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Trigger Product IDs</InputLabel>
            <Select
              multiple
              value={formData.trigger.productIds || []}
              onChange={(e) =>
                handleTriggerChange("productIds", e.target.value)
              }
              input={<OutlinedInput label="Trigger Product IDs" />}
              renderValue={(selected) =>
                selected
                  .map((id) => {
                    const match = allProducts.find((p) => p._id === id);
                    return match ? match.displayName || match.name : id;
                  })
                  .join(", ")
              }>
              {allProducts.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  <Checkbox
                    checked={(formData.trigger.productIds || []).includes(
                      p._id
                    )}
                  />
                  <ListItemText primary={p.displayName || p.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleChange("type", e.target.value)}
              required>
              <MenuItem value="edible">Edible</MenuItem>
              <MenuItem value="accessory">Accessory</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Display Category"
            value={formData.displayCategory}
            onChange={(e) => handleChange("displayCategory", e.target.value)}
            fullWidth
            helperText="Shown to users (e.g. Add-On, Extra, Sweet Treat)"
            required
          />

          <TextField
            label="Minimum Subtotal Required ($)"
            type="number"
            value={formData.trigger.minSubtotal || ""}
            onChange={(e) =>
              handleTriggerChange("minSubtotal", parseFloat(e.target.value))
            }
            fullWidth
          />

          <TextField
            label="Max Per Order"
            type="number"
            value={formData.maxPerOrder}
            onChange={(e) =>
              handleChange("maxPerOrder", parseInt(e.target.value))
            }
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel shrink>Is Active</InputLabel>
            <Select
              value={formData.isActive ? "true" : "false"}
              onChange={(e) =>
                handleChange("isActive", e.target.value === "true")
              }>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Notes (internal use)"
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {initialData ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpsellItemFormModal;
